import { createSlice } from "@reduxjs/toolkit";
// import { emitSlideAdd, emitSpeakerNotes } from "../Socket/Socket";
import { slideAPI } from "./api";
import { fabClasses } from "@mui/material";
import { uploadToCloudinary } from "../utils/cloudinary";
import { getManualOfflineMode } from "../utils/helper";

const {
  saveMirrorInDatabase,
  updateSlideBackgroundInDatabase,
  updateElementStyleInDatabase,
  saveCurrentSlideIdInDatabase } = slideAPI;

const initialState = {
  /******Data structure */
  presentationId: "",
  currentSlideId: "",
  isImporting: false,
  // currentElementId: "",
  presentMode: false,
  mirrorSlides: {},
  slides: {},
  documentInformation: {},
  isReadOnly: false,
  userInformation: {},
  darkMode: true,
  isMasterSlide: false,
  masterSlideId: '',
  masterSlideElementId: '',
  deletedElements: {},
  savingData: {
    isSaving: '',
    lastSavedDateTime: "",
    fileSize: 0,
  },
  isExternal: false,
  isExternalAllow: false,
  elementProperties: {},
  isAuthenticated: false,
  // Seeded from localStorage so a reload keeps the user's choice.
  isManualOffline: getManualOfflineMode(),
  isTemplateDocument: null,
  templateData: {},
  admins: [],
  activeSmartArtId: null,
  // Present-mode playhead: index of the last-played click group on the current slide.
  // -1 means nothing has played yet, so animated objects start hidden.
  animationStepIndex: -1,
  // Incremented by the toolbar's Preview button; a change triggers one playthrough
  // in the editor. Null means no preview is running.
  animationPreviewToken: null,
  // Whether the docked Animations pane is showing. Lives here because the toolbar
  // trigger and the pane itself sit in unrelated parts of the tree.
  animationPaneOpen: false,
};



const slideSlice = createSlice({
  name: "slide",
  initialState,
  reducers: {
    setIsImporting(state, action) {
      state.isImporting = Boolean(action.payload);
    },
    addSlide(slideState, actions) {
      const { slideId, slideData } = actions.payload;
      // if(!slideState.slide){
      //   slideState.slides = {};
      // }
      slideState.slides[slideId] = slideData;
      // slideState.mirrorSlides[slideId] =  "";
      // slideState.currentSlideId = slideId;
      // addNewSlideInDatabase(slideState.presentationId,slideId,slideData);
    }, // in reducer method parameter we get latest updated state 
    updateSlide(state, action) {
      const { slideId, slideData } = action.payload;
      state.slides[slideId] = slideData;
    },
    updateSlidesData(state, action) {
      const { slides, mirrorSlides } = action.payload;
      if (slides) {
        state.slides = slides;
        if (mirrorSlides) {
          state.mirrorSlides = mirrorSlides;
        }
      }
    },
    updateIsSaving(state, action) {
      state.savingData = { ...action.payload };
    },
    setDocumentInformation(state, action) {
      state.documentInformation = action.payload;

      const searchParams = new URLSearchParams(document.location.search)
      const isReadOnlyQuery = searchParams.get("readOnly")
      if (isReadOnlyQuery == "true" || action.payload.rightType == 1) {
        state.isReadOnly = true;
      }
    },
    setIsExternal(state, action) {
      state.isExternal = action.payload;
    },
    setIsExternalAllow(state, action) {
      state.isExternalAllow = action.payload;
    },
    setIsReadOnly(state, action) {
      state.isReadOnly = action.payload;
    },
    setManualOffline(state, action) {
      state.isManualOffline = Boolean(action.payload);
    },
    setDarkMode(state, actions) {
      state.darkMode = actions.payload;
    },
    setIsMasterSlide(state, action) {
      state.isMasterSlide = action.payload;
    },
    setMasterSlideId(state, action) {
      state.masterSlideId = action.payload;
    },
    setMasterSlideIdElementId(state, action) {
      state.masterSlideElementId = action.payload;
    },
    addDeletedElementId(state, action) {
      const { elementId, elementData } = action.payload;
      state.deletedElements[elementId] = elementData;
    },
    removeDeletedElementId(state, action) {
      delete state.deletedElements[action.payload];
    },
    updateElement(state, action) {
      const { slideId, elementId, elementData, type } = action.payload;
      if (type === 'textbox' && slideId && state.slides[slideId] && state.slides[slideId].textBox[elementId]) {
        state.slides[slideId].textBox[elementId].delta = elementData.delta;
        state.slides[slideId].textBox[elementId].styles = elementData.styles;
      }
      else if (type === 'image' && slideId && state.slides[slideId] && state.slides[slideId].image[elementId]) {
        state.slides[slideId].image[elementId] = elementData;
      }
      else if (type === 'shape' && slideId && state.slides[slideId] && state.slides[slideId].shapes[elementId]) {
        state.slides[slideId].shapes[elementId] = elementData;
      }
      else if (type === 'wordart' && slideId && state.slides[slideId]?.wordArtTextObjects?.[elementId]) {
        state.slides[slideId].wordArtTextObjects[elementId] = elementData;
      }
      else if (type === 'smartart' && slideId && state.slides[slideId]?.smartArtObjects?.[elementId]) {
        state.slides[slideId].smartArtObjects[elementId] = elementData;
      }
    },
    setUserInformation(state, action) {
      state.userInformation = action.payload;
    },
    setElementProperties(state, action) {
      state.elementProperties = action.payload;
    },
    setIsAuthenticated(state, action) {
      state.isAuthenticated = action.payload;
    },
    setIsTemplateDocument(state, action) {
      state.isTemplateDocument = action.payload;
    },
    setTemplateData(state, action) {
      state.templateData = action.payload;
    },
    setAdmins(state, action) {
      state.admins = action.payload;
    },
    createNewPresentation(slideState, actions) {
      let { currentSlideId, presentMode, mirrorSlides, slides } = actions.payload;
      if (!mirrorSlides) {
        mirrorSlides = {};
      }
      // slideState.presentationId = presentationId;
      slideState.currentSlideId = currentSlideId;
      slideState.presentMode = presentMode;
      slideState.mirrorSlides = mirrorSlides;
      if (slides) {
        Object.keys(slides).forEach((item) => {
          if (!slides[item].image) {
            slides[item].image = {};
          }
          if (!slides[item].shapes) {
            slides[item].shapes = {};
          }
          if (!slides[item].wordArtTextObjects) {
            slides[item].wordArtTextObjects = {};
          }
          if (!slides[item].speakerNotes) {
            slides[item].speakerNotes = "";
          }
          // if(!slides[item].textBox){
          //   slides[item].textBox = {};
          // }
          // if(!slides[item].textBoxIds){
          //   slides[item].textBoxIds = [];
          // }
        });
      }
      slideState.slides = slides;
    },
    addTextBox(slideState, actions) {
      const { textId, style, currentSlideId } = actions.payload;
      slideState.slides[currentSlideId].textBoxIds.push(textId);
      slideState.slides[currentSlideId].textBox[textId] = {
        delta: actions.payload.delta ? actions.payload.delta : {
          ops: [{ insert: "" }]
        },
        styles: style
      }
      // createNewElementInDatabase(slideState.presentationId,currentSlideId,textId,{ delta: {
      //   ops: [{insert:""}]
      // },styles: style}, 'text');
    },
    // addData(slideState, actions) {
    //   slideState.slideData[slideState.currentSlideIndex].text[slideState.currentTextBoxIndex].value =
    //     actions.payload;
    // },
    setCurrentSlide(state, actions) {
      state.currentSlideId = actions.payload;
      // A new slide always starts before its first animation.
      state.animationStepIndex = -1;
      if (!state.isImporting && !state.isMasterSlide && !state.isReadOnly) {
        saveCurrentSlideIdInDatabase(state.presentationId, actions.payload);
      }
    },
    setPresentMode(state, actions) {
      state.presentMode = actions.payload;
      // Entering or leaving present mode rewinds the current slide's animations.
      state.animationStepIndex = -1;
      // Presenting hides the editor chrome; the pane must not linger behind it.
      if (actions.payload) state.animationPaneOpen = false;
    },
    setSlideBackgroundColor(state, actions) {
      const { slideId, backgroundColor, isChangedByUser } = actions.payload;
      state.slides[slideId].slideBackgroundColor = backgroundColor;
      if (isChangedByUser) {
        state.slides[slideId].additionalData = {
          ...state.slides[slideId].additionalData,
          isSlideColorChanged: isChangedByUser
        };
      }
      // if(!state.currentSlideId.includes('master')){
      //   updateSlideBackgroundInDatabase(state.presentationId, slideId, backgroundColor);
      // }
    },
    // setCurrentElement(state, actions) {
    //   state.currentElementId = actions.payload;
    // },
    updateStyle(slideState, actions) {
      const { slideId, textBoxId, updatedStyle } = actions.payload; // textboxid can also be shape id or image id 
      if (textBoxId.includes('text') || textBoxId.includes('title')) {
        slideState.slides[slideId].textBox[textBoxId].styles = updatedStyle;
        // if(!slideState.isMasterSlide){
        //   updateElementStyleInDatabase(slideState.presentationId, slideId, textBoxId, updatedStyle, 'text');
        // }
      }
      else if (textBoxId.includes('image')) {
        slideState.slides[slideId].image[textBoxId].styles = updatedStyle;
        // if(!slideState.isMasterSlide){
        //   updateElementStyleInDatabase(slideState.presentationId, slideId, textBoxId, updatedStyle, 'image');
        // }
      }
      else if (textBoxId.includes('shape')) {
        slideState.slides[slideId].shapes[textBoxId].styles = updatedStyle;
        // if(!slideState.isMasterSlide){
        //   updateElementStyleInDatabase(slideState.presentationId, slideId, textBoxId, updatedStyle, 'shape');
        // }
      }
      else if (textBoxId.includes('wordart')) {
        slideState.slides[slideId].wordArtTextObjects[textBoxId].styles = updatedStyle;
      }
      else if (textBoxId.includes('smartart')) {
        if (slideState.slides[slideId]?.smartArtObjects?.[textBoxId]) {
          slideState.slides[slideId].smartArtObjects[textBoxId].styles = updatedStyle;
        }
      }
      // textBoxId.substring(0,5) !== 'image' ? slideState.slides[slideState.currentSlideId].textBox[textBoxId].styles = updatedStyle : slideState.slides[slideId].image[textBoxId].styles = updatedStyle;
    },
    // removeStyle(slideState,actions){
    //   delete slideState.slideData[slideState.currentSlideIndex].text[slideState.currentTextBoxIndex].styles[actions.payload]
    //   // console.log(slideState.slideData[slideState.currentSlideIndex].text[slideState.currentTextBoxIndex].styles);
    // },
    updateDelta(state, actions) {
      // console.log("inside update Delta",actions.payload)
      const { slideId, content, activeQuillInstanceId } = actions.payload;
      if (slideId && state.slides[slideId] && state.slides[slideId].textBox[activeQuillInstanceId] && state.slides[slideId].textBox[activeQuillInstanceId].delta)
        state.slides[slideId].textBox[activeQuillInstanceId].delta = content;
      // updateElementTextInDatabase(state.presentationId, slideId, activeQuillInstanceId, content);
    },
    addImage(state, actions) {
      const { imageId, imageb64, style, currentSlideId } = actions.payload;
      state.slides[currentSlideId].imageIds.push(imageId);
      state.slides[currentSlideId].image[imageId] = { imageb64: imageb64, styles: style }
      // console.log(state.slides[state.currentSlideId].image);
      // createNewElementInDatabase(state.presentationId, currentSlideId, imageId, {imageb64: imageb64, styles: {width: '20%', height:'20%', position: 'absolute', top:'50%', left: '50%'}}, 'image');
    },
    addSpeakerNotes(state, actions) {
      const { slideId, content } = actions.payload;
      state.slides[slideId].speakerNotes = content;
      // console.log(emitFlag);
      // if(emitFlag){
      //   emitSpeakerNotes({roomId: state.presentationId, notesData: {slideId: slideId, content: content}});
      //   updateSpeakerNotesInDatabase(state.presentationId, slideId, content);
      // }
      // updateSpeakerNotesInDatabase(state.presentationId,slideId, content);
    },

    importPresentation(state) {
      state.currentSlideId = "";
      state.mirrorSlides = {};
      state.slides = {};
      // state.slides = action.payload;

      // console.log(state.slides);
      // for(const prop in state.slides){
      //   state.currentSlideId = prop;
      //   console.log("property=>>",prop, "currentSlide===>",state.currentSlideId);
      // }
    },
    saveMirror(state, actions) {
      const { dataId, mirrorSlideDataUrl } = actions.payload;
      if (dataId) {
        if (!state.currentSlideId.includes('master')) {
          state.mirrorSlides[dataId] = mirrorSlideDataUrl;
        }
      }
    },
    removeTextBox(state, action) {
      const { slideId, textBoxId } = action.payload;
      state.slides[slideId].textBoxIds = state.slides[slideId].textBoxIds.filter(textBox => textBox !== textBoxId);
      delete state.slides[slideId].textBox[textBoxId];
      // deleteElementFromDatabase(state.presentationId,slideId,textBoxId, 'text');
    },
    removeImage(state, action) {
      const { slideId, imageId } = action.payload;
      state.slides[slideId].imageIds = state.slides[slideId].imageIds.filter(image => image !== imageId);
      delete state.slides[slideId].image[imageId];
      // deleteElementFromDatabase(state.presentationId, slideId, imageId, 'image');
    },
    removeSlide(state, action) {
      const slideId = action.payload;
      const slideKeys = Object.keys(state.slides).map(key => key);
      const nextIndex = slideKeys.indexOf(slideId) === slideKeys.length - 1 ? slideKeys.indexOf(slideId) - 1 : slideKeys.indexOf(slideId) + 1;
      if (state.currentSlideId === action.payload) {
        state.currentSlideId = slideKeys[nextIndex] ? slideKeys[nextIndex] : "";
      }

      delete state.mirrorSlides[slideId];
      delete state.slides[slideId];
      // deleteSlideFromDatabase(state.presentationId,slideId, state.currentSlideId);
    },
    addShape(slideState, actions) {
      const { shapeId, style, currentSlideId } = actions.payload;
      // slideState.slides[slideState.currentSlideId].textBoxIds.push(textId)
      slideState.slides[currentSlideId].shapes[shapeId] = {
        delta: actions.payload.delta ? actions.payload.delta : {
          ops: [{ insert: "" }]
        },
        styles: style
      }
      // createNewElementInDatabase(slideState.presentationId, currentSlideId, shapeId,{ delta: {
      //   ops: [{insert:""}]
      // },styles: style},'shape');
    },
    removeShape(slideState, actions) {
      const { slideId, shapeId } = actions.payload;
      delete slideState.slides[slideId].shapes[shapeId];
      // deleteElementFromDatabase(slideState.presentationId,slideId,shapeId, 'shape');
    },
    updateShapeText(state, actions) {
      const { slideId, content, shapeId } = actions.payload;
      if (state.slides[slideId] && state.slides[slideId].shapes[shapeId] && state.slides[slideId].shapes[shapeId].delta)
        state.slides[slideId].shapes[shapeId].delta = content;
      // updateShapeTextInDatabase(state.presentationId, slideId, shapeId, content);
    },
    addWordArtTextObject(slideState, actions) {
      const { wordArtId, style, currentSlideId, data } = actions.payload;
      if (!slideState.slides[currentSlideId].wordArtTextObjects) {
        slideState.slides[currentSlideId].wordArtTextObjects = {};
      }
      slideState.slides[currentSlideId].wordArtTextObjects[wordArtId] = {
        ...data,
        styles: style,
      };
    },
    removeWordArtTextObject(slideState, actions) {
      const { slideId, wordArtId } = actions.payload;
      if (slideState.slides[slideId]?.wordArtTextObjects) {
        delete slideState.slides[slideId].wordArtTextObjects[wordArtId];
      }
    },
    updateWordArtText(state, actions) {
      const { slideId, wordArtId, content } = actions.payload;
      if (
        state.slides[slideId]?.wordArtTextObjects?.[wordArtId]
      ) {
        state.slides[slideId].wordArtTextObjects[wordArtId].delta = content;
      }
    },
    addSmartArtObject(slideState, actions) {
      const { smartArtId, style, currentSlideId, data } = actions.payload;
      if (!slideState.slides[currentSlideId].smartArtObjects) {
        slideState.slides[currentSlideId].smartArtObjects = {};
      }
      slideState.slides[currentSlideId].smartArtObjects[smartArtId] = {
        ...data,
        styles: style,
      };
    },
    removeSmartArtObject(slideState, actions) {
      const { slideId, smartArtId } = actions.payload;
      if (slideState.slides[slideId]?.smartArtObjects) {
        delete slideState.slides[slideId].smartArtObjects[smartArtId];
      }
    },
    updateSmartArtNodes(state, actions) {
      const { slideId, smartArtId, nodes } = actions.payload;
      if (state.slides[slideId]?.smartArtObjects?.[smartArtId]) {
        state.slides[slideId].smartArtObjects[smartArtId].nodes = nodes;
      }
    },
    setActiveSmartArt(state, actions) {
      state.activeSmartArtId = actions.payload;
    },
    updateSmartArtLayout(state, actions) {
      const { slideId, smartArtId, layout, variant, theme } = actions.payload;
      const obj = state.slides[slideId]?.smartArtObjects?.[smartArtId];
      if (obj) {
        if (layout !== undefined) obj.layout = layout;
        if (variant !== undefined) obj.variant = variant;
        if (theme !== undefined) obj.theme = theme;
      }
    },
    /**
     * Animations live on the slide, not the object, because PowerPoint's model is a single
     * ordered sequence per slide. `steps` order IS the play order.
     */
    addAnimation(state, actions) {
      const { slideId, step } = actions.payload;
      const slide = state.slides[slideId];
      if (!slide || !step) return;
      if (!slide.animations) slide.animations = { steps: [], passthrough: [] };
      if (!Array.isArray(slide.animations.steps)) slide.animations.steps = [];
      // One authored step per object, matching the toolbar's single-selection model:
      // re-applying an effect replaces the existing one and keeps its sequence position.
      const existing = slide.animations.steps.findIndex((s) => s.targetId === step.targetId);
      if (existing === -1) slide.animations.steps.push(step);
      else slide.animations.steps[existing] = { ...step, id: slide.animations.steps[existing].id };
    },
    updateAnimation(state, actions) {
      const { slideId, stepId, patch } = actions.payload;
      const steps = state.slides[slideId]?.animations?.steps;
      if (!Array.isArray(steps)) return;
      const index = steps.findIndex((s) => s.id === stepId);
      if (index !== -1) steps[index] = { ...steps[index], ...patch };
    },
    removeAnimation(state, actions) {
      const { slideId, stepId } = actions.payload;
      const animations = state.slides[slideId]?.animations;
      if (!Array.isArray(animations?.steps)) return;
      animations.steps = animations.steps.filter((s) => s.id !== stepId);
    },
    /** Drop any animation targeting an object that was deleted from the slide. */
    removeAnimationsForTarget(state, actions) {
      const { slideId, targetId } = actions.payload;
      const animations = state.slides[slideId]?.animations;
      if (!Array.isArray(animations?.steps)) return;
      animations.steps = animations.steps.filter((s) => s.targetId !== targetId);
    },
    reorderAnimations(state, actions) {
      const { slideId, fromIndex, toIndex } = actions.payload;
      const steps = state.slides[slideId]?.animations?.steps;
      if (!Array.isArray(steps)) return;
      if (fromIndex < 0 || fromIndex >= steps.length || toIndex < 0 || toIndex >= steps.length) return;
      const [moved] = steps.splice(fromIndex, 1);
      steps.splice(toIndex, 0, moved);
    },
    /** Present-mode playhead: which click group of the current slide has been reached. */
    setAnimationStepIndex(state, actions) {
      state.animationStepIndex = actions.payload;
    },
    /** Toolbar Preview: bump the token to replay the current slide's animations once. */
    startAnimationPreview(state) {
      state.animationPreviewToken = Date.now();
    },
    endAnimationPreview(state) {
      state.animationPreviewToken = null;
    },
    /** Toolbar toggle for the docked Animations pane. */
    setAnimationPaneOpen(state, actions) {
      state.animationPaneOpen = actions.payload;
    },
    setPresentationId(state, actions) {
      state.presentationId = actions.payload;
    },
    loadPresentation(state, actions) {
      let { currentSlideId, mirrorSlides, slides } = actions.payload;
      let existingMasterSlideId;

      if (slides) {
        existingMasterSlideId = Object.keys(slides).find(slideId => slideId.toLowerCase().includes("master"));
        if (currentSlideId.toLowerCase().includes("master")) {
          const slideIds = Object.keys(slides);
          let i = slideIds.length - 1;
          while (i >= 0 && slideIds[i].toLowerCase().includes("master")) {
            i--;
          }
          if (i >= 0) {
            currentSlideId = slideIds[i];
          }
        }
        Object.keys(slides).forEach((item) => {
          if (!slides[item].text) {
            slides[item].text = {};
          }
          if (!slides[item].image) {
            slides[item].image = {};
          }
          if (!slides[item].shapes) {
            slides[item].shapes = {};
          }
          if (!slides[item].textBox) {
            slides[item].textBox = {};
          }
          if (!slides[item].textBoxIds) {
            slides[item].textBoxIds = [];
          }
          if (!slides[item].wordArtTextObjects) {
            slides[item].wordArtTextObjects = {};
          }
          // if(!slides[item].textBox){
          //   slides[item].textBox = {};
          // }
        })
      } else {
        slides = {}
      }
      state.currentSlideId = currentSlideId;
      if (mirrorSlides) {
        state.mirrorSlides = mirrorSlides;
      }
      state.slides = slides;
      if (existingMasterSlideId) {
        state.masterSlideId = existingMasterSlideId;
        //  state.isMasterSlide = true;
      }


      // console.log("slides",slides,currentSlideId);
      // state.currentSlideId = "hahatest";
      // if (slides.image === undefined) {
      //   state.slides = slides;  
      //   state.slides[currentSlideId].image = {};
      // } else {
      // }
      // if(slides.speakerNotes === undefined){
      //   state.slides[currentSlideId].speakerNotes = ""

      // }


    },
    moveSlideToBeginning: (state, action) => {
      const slideId = action.payload;
      const slideOrder = Object.keys(state.slides);
      const index = slideOrder.indexOf(slideId);
      if (index !== -1) {
        const [movedSlide] = slideOrder.splice(index, 1);
        // slideOrder.unshift(movedSlide);
        // const movedSlides = state.slides[movedSlide];
        // state.slides = {
        //   [slideId]: movedSlides,
        //   ...state.slides,
        // }
        const movedMirrorSlide = state.mirrorSlides[movedSlide];
        delete state.mirrorSlides[movedSlide];
        state.mirrorSlides = {
          [slideId]: movedMirrorSlide,
          ...state.mirrorSlides,
        };
        // saveMirrorInDatabase(state.presentationId,slideId, state.mirrorSlides)
      }
    },
    moveSlideToEnd: (state, action) => {
      const slideId = action.payload;
      const slideOrder = Object.keys(state.slides);
      const index = slideOrder.indexOf(slideId);
      if (index !== -1) {
        const [movedSlide] = slideOrder.splice(index, 1);
        //  slideOrder.push(movedSlide);
        // const updatedSlides = {};
        // slideOrder.forEach((id) => {
        //   updatedSlides[id] = state.slides[id];
        // });
        // state.slides = updatedSlides;
        const movedMirrorSlide = state.mirrorSlides[movedSlide];
        delete state.mirrorSlides[movedSlide];
        state.mirrorSlides = {
          ...state.mirrorSlides,
          [slideId]: movedMirrorSlide,
        };
      }
    },
    removeDocumentMember(state, action) {
      state.documentInformation.members = state.documentInformation?.members?.filter((member) => member.id !== action.payload);
    }
  },
});

export const saveMirrorToCloudinary = ({ dataId, mirrorSlideDataUrl, presentationId, uploadToCloud = false }) => {
  return async (dispatch, getState) => {
    const state = getState().slide;
    if (!dataId || state.currentSlideId.includes('master')) return;

    // Immediately set the base64 in Redux for instant UI feedback
    dispatch(slideAction.saveMirror({ dataId, mirrorSlideDataUrl }));

    if (state.isImporting || state.isMasterSlide || state.isReadOnly) return;

    if (!uploadToCloud) {
      saveMirrorInDatabase(presentationId, dataId, mirrorSlideDataUrl);
      return;
    }

    try {
      const cloudinaryUrl = await uploadToCloudinary(mirrorSlideDataUrl);
      // Update Redux with the Cloudinary URL
      dispatch(slideAction.saveMirror({ dataId, mirrorSlideDataUrl: cloudinaryUrl }));
      // Save the Cloudinary URL to the database
      saveMirrorInDatabase(presentationId, dataId, cloudinaryUrl);
    } catch (error) {
      console.error('Failed to upload mirror slide to Cloudinary:', error);
      // Fallback: save the base64 directly to the database
      saveMirrorInDatabase(presentationId, dataId, mirrorSlideDataUrl);
    }
  };
};

export const importPresentation = (param) => {
  return async (dispatch) => {
    dispatch(slideAction.setIsImporting(true));
    try {
      dispatch(slideAction.importPresentation());
      const slideEntries = Object.entries(param);
      const importedSlides = [];
      for (let i = 0; i < slideEntries.length; i++) {
        const [slideId, slideData] = slideEntries[i];
        dispatch(slideAction.addSlide({ slideId, slideData }));
        dispatch(slideAction.setCurrentSlide(slideId));
        importedSlides.push({ slideId, slideData });

        await new Promise((resolve) => setTimeout(resolve, 700));
      }
      // return importedSlides;
    } finally {
      dispatch(slideAction.setIsImporting(false));
    }
  };
};

//   export const loadPresentation = (param) => {
//     let i = 0;
//     return async(dispatch) => {
//         dispatch(slideAction.loadSlide());
//         function myLoop() {         //  create a loop function
//           setTimeout(function() {   //  call a 3s setTimeout when the loop is called
//             dispatch(slideAction.addSlide({slideId:`slide${i}`, slideData: param[`slide${i}`]}))
//             i++;                    //  increment the counter
//             if (i < Object.keys(param).length) {           //  if the counter < 10, call the loop function
//               myLoop();             //  ..  again which will trigger another 
//             }                       //  ..  setTimeout()
//           }, 700)
//         }
//         myLoop();
//     };
// }





export const slideAction = slideSlice.actions; // export slideActions which have reducer method names as keys to access actions

export default slideSlice.reducer;